import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/apiClient";
import { Sparkles, Clock, Package, Mountain, ShieldCheck, Leaf, ArrowRight, BadgeCheck, AlertCircle, Users, ChevronDown, ChevronUp } from "lucide-react";
import { LineageCard } from "../../pages/LineageAtlas";
import { breederUrl } from "../../lib/vendorLinks";
import { StrainProofStatsProvider } from "../StrainProofStats";

/**
 * v3.80 — Sectioned Strain Archive landing.
 *
 * Replaces the previous "search-first → giant card grid" archive view with
 * organized browse sections (Featured → Recent → On CannaForge → Browse by
 * Family / Classification / Preservation → Full search). Renders sections
 * only when their underlying public-approved data has results so the page
 * never shows empty placeholders to public users.
 */
const CLASS_LABELS = {
  indica: "Indica",
  indica_dominant: "Indica-dominant",
  hybrid: "Hybrid",
  balanced: "Balanced",
  sativa_dominant: "Sativa-dominant",
  sativa: "Sativa",
  cbd: "CBD",
  cbg: "CBG",
  high_thc: "High-THC",
  high_cbd: "High-CBD",
  ruderalis: "Ruderalis",
};

export default function ArchiveSections({ onJumpToSearch }) {
  const [data, setData] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [recentExpanded, setRecentExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/lineage/archive-sections", { params: { limit: 8 } })
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    // v3.86 — pull the breeder directory so we can render the "View breeder
    // map and genetic list" section under Recently Added. Failure here is
    // soft — the section just won't render.
    api.get("/vendors").then((r) => { if (alive) setVendors(r.data || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  if (loading) {
    return <div className="p-10 text-center text-sm" style={{ color: "var(--text-muted)" }} data-testid="archive-sections-loading">Loading archive sections…</div>;
  }
  if (!data || data.total_public_records === 0) {
    return null; // empty state already handled by the parent
  }

  // v3.99ae — Collect every strain id rendered in any section into a single
  // batch so the proof-stats endpoint fires ONCE for the whole archive page.
  const allIds = [
    ...(data.featured || []),
    ...(data.recent || []),
    ...(data.onsite || []),
    ...Object.values(data.classifications || {}).flat(),
    ...Object.values(data.families || {}).flat(),
    ...Object.values(data.preservation || {}).flat(),
  ].map((l) => l?.id).filter(Boolean);

  return (
    <StrainProofStatsProvider strainIds={allIds}>
    <div className="space-y-12" data-testid="archive-sections">
      {/* FEATURED */}
      {data.featured?.length > 0 && (
        <Section icon={Sparkles} title="Featured lineages"
                 subtitle="Most-saved verified records · curated by the community"
                 testid="archive-section-featured">
          <CardGrid items={data.featured} />
        </Section>
      )}

      {/* RECENTLY ADDED — capped at 6 with a "Show more" expander so the page
          stays scannable when the catalog grows. */}
      {data.recent?.length > 0 && (
        <Section icon={Clock} title="Recently added"
                 subtitle={`Newest record${data.recent.length === 1 ? "" : "s"} in the archive · ${data.recent.length} shown`}
                 testid="archive-section-recent">
          <CardGrid items={recentExpanded ? data.recent : data.recent.slice(0, 6)} />
          {data.recent.length > 6 && (
            <div className="mt-4 flex justify-center">
              <button onClick={() => setRecentExpanded((x) => !x)}
                      className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em] px-3 py-2 border border-[var(--border)] hover:border-[var(--gold)] transition-colors"
                      style={{ color: "var(--text-2)", borderRadius: 2 }}
                      data-testid="archive-recent-toggle">
                {recentExpanded
                  ? (<><ChevronUp className="w-3 h-3"/> Show less</>)
                  : (<><ChevronDown className="w-3 h-3"/> Show {data.recent.length - 6} more</>)}
              </button>
            </div>
          )}
        </Section>
      )}

      {/* BREEDER DIRECTORY ENTRY POINT — small logo + name list of every
          breeder on the platform. Clicking any pill jumps straight to their
          storefront, which now hosts its own per-breeder genetics map. */}
      {vendors.length > 0 && (
        <Section icon={Users} title="View breeder map and genetic list"
                 subtitle={`Explore each breeder's own lineage map and trait wheel · ${vendors.length} active breeder${vendors.length === 1 ? "" : "s"}`}
                 testid="archive-section-breeders">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
            {vendors.map((v) => (
              <Link key={v.id}
                    to={breederUrl(v)}
                    className="group bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] transition-colors p-3 flex items-center gap-3"
                    style={{ borderRadius: 2 }}
                    data-testid={`archive-breeder-${v.store_slug || v.id}`}>
                <div className="shrink-0 w-9 h-9 bg-black flex items-center justify-center overflow-hidden border border-[var(--border)]"
                     style={{ borderRadius: 2 }}>
                  {v.logo_url ? (
                    <img src={v.logo_url} alt="" className="max-w-[80%] max-h-[80%] object-contain" loading="lazy" />
                  ) : (
                    <Leaf className="w-4 h-4" style={{ color: "var(--gold)" }} />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-serif text-sm leading-tight truncate group-hover:text-[var(--gold)] transition-colors"
                       style={{ color: "var(--text)" }}>
                    {v.store_name}
                  </div>
                  {v.tagline && (
                    <div className="text-[9px] font-mono uppercase tracking-[0.14em] truncate"
                         style={{ color: "var(--text-muted)" }}>
                      {v.tagline}
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* ON CANNAFORGE */}
      {data.on_cannaforge?.length > 0 && (
        <Section icon={Package} title="Available on CannaForge"
                 subtitle="Lineages tied to active catalog listings"
                 testid="archive-section-on-cannaforge">
          <CardGrid items={data.on_cannaforge} />
        </Section>
      )}

      {/* BROWSE BY FAMILY */}
      {data.by_family?.length > 0 && (
        <Section icon={Leaf} title="Browse by family"
                 subtitle="Genetic family groupings derived from lineage records"
                 testid="archive-section-family">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 md:gap-3">
            {data.by_family.map((f) => (
              <button key={f.label}
                      onClick={() => onJumpToSearch?.(f.label)}
                      className="text-left bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] px-4 py-3 transition-colors group"
                      data-testid={`archive-family-${slugify(f.label)}`}>
                <div className="font-serif text-base leading-tight mb-1 group-hover:text-[var(--gold)] transition-colors"
                     style={{ color: "var(--text)" }}>{f.label}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.16em]"
                     style={{ color: "var(--text-muted)" }}>{f.count} record{f.count === 1 ? "" : "s"}</div>
              </button>
            ))}
          </div>
        </Section>
      )}

      {/* BROWSE BY CLASSIFICATION */}
      {Object.keys(data.by_classification || {}).length > 0 && (
        <Section icon={BadgeCheck} title="Browse by classification"
                 subtitle="Indica · Hybrid · Sativa spectrum"
                 testid="archive-section-classification">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
            {Object.entries(data.by_classification)
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => (
                <button key={k}
                        onClick={() => onJumpToSearch?.(null, { classification: k })}
                        className="text-left bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] px-4 py-3 transition-colors"
                        data-testid={`archive-classification-${k}`}>
                  <div className="font-serif text-base leading-tight mb-1" style={{ color: "var(--text)" }}>{CLASS_LABELS[k] || k.replace(/_/g, " ")}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>{v} record{v === 1 ? "" : "s"}</div>
                </button>
              ))}
          </div>
        </Section>
      )}

      {/* BROWSE BY PRESERVATION */}
      {Object.values(data.by_preservation || {}).some((v) => v > 0) && (
        <Section icon={Mountain} title="Browse by preservation type"
                 subtitle="Landrace · Heirloom · Preservation"
                 testid="archive-section-preservation">
          <div className="grid grid-cols-3 gap-2 md:gap-3">
            {[
              { key: "landrace", label: "Landraces", Icon: Mountain },
              { key: "heirloom", label: "Heirloom Lines", Icon: ShieldCheck },
              { key: "preservation", label: "Preservation Work", Icon: Sparkles },
            ].map(({ key, label, Icon }) => {
              const c = data.by_preservation[key] || 0;
              if (c === 0) return null;
              return (
                <button key={key}
                        onClick={() => onJumpToSearch?.(null, { preservation: key })}
                        className="text-left bg-[var(--surface)] border border-[var(--border)] hover:border-[var(--gold)] px-4 py-4 transition-colors"
                        data-testid={`archive-preservation-${key}`}>
                  <Icon className="w-4 h-4 mb-2" style={{ color: "var(--gold)" }} />
                  <div className="font-serif text-base leading-tight mb-1" style={{ color: "var(--text)" }}>{label}</div>
                  <div className="text-[10px] font-mono uppercase tracking-[0.16em]" style={{ color: "var(--text-muted)" }}>{c} record{c === 1 ? "" : "s"}</div>
                </button>
              );
            })}
          </div>
        </Section>
      )}

      {/* JUMP TO FULL SEARCH */}
      <div className="bg-[var(--surface)] border border-[var(--border)] p-5 md:p-6 flex flex-wrap items-center gap-3"
           data-testid="archive-jump-to-search">
        <Leaf className="w-5 h-5" style={{ color: "var(--gold)" }} />
        <div className="flex-1 min-w-[200px]">
          <div className="font-display text-lg leading-tight" style={{ color: "var(--text)" }}>
            Looking for a specific strain?
          </div>
          <div className="text-xs" style={{ color: "var(--text-muted)" }}>
            Full searchable archive of {data.total_public_records} approved record{data.total_public_records === 1 ? "" : "s"}.
          </div>
        </div>
        <button onClick={() => onJumpToSearch?.()}
                className="btn-primary inline-flex items-center gap-2 text-sm"
                data-testid="archive-jump-to-search-btn">
          Open full search <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
    </StrainProofStatsProvider>
  );
}

function Section({ icon: Icon, title, subtitle, testid, children }) {
  return (
    <section data-testid={testid}>
      <div className="flex items-end justify-between mb-4">
        <div>
          <h2 className="font-display text-xl md:text-2xl tracking-tighter flex items-center gap-2 leading-tight"
              style={{ color: "var(--text)" }}>
            {Icon && <Icon className="w-4 h-4" style={{ color: "var(--gold)" }} />} {title}
          </h2>
          {subtitle && <div className="text-[11px] font-mono uppercase tracking-[0.16em] mt-1" style={{ color: "var(--text-muted)" }}>{subtitle}</div>}
        </div>
      </div>
      {children}
    </section>
  );
}

function CardGrid({ items }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
      {items.map((l) => <LineageCard key={l.id} item={l} />)}
    </div>
  );
}

function slugify(s) {
  return String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
